import { Reveal } from "../anim";
import { motion } from "framer-motion";
import { useSite, t } from "@/lib/site";

const ITEMS = [
  {
    n: "01",
    role: "The Buyer",
    icon: "/buyer-s.png",
    img: "https://images.unsplash.com/photo-1760812655704-7dc53dd36194?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHx3b21hbiUyMGxvb2tpbmclMjBhdCUyMGFydGlzYW4lMjBzaG9wJTIwYmFza2V0c3xlbnwwfHx8fDE3OTM1NjcwMjZ8MA&ixlib=rb-4.1.0&q=85",
    text: "Buyers hunt endlessly for something new to bring to market—only to find the same mass-produced products everywhere. The challenge? Finding products that are truly original and worth launching.",
    color: "#FF8473",
    anim: { x: [0, 4, 0, -4, 0], y: [0, -11, 0, -6, 0], rotate: [0, -4, 0, 4, 0] },
    dur: 0.75,
  },
  {
    n: "02",
    role: "The Manufacturer",
    icon: "/manufacturer-s.png",
    img: "https://images.unsplash.com/photo-1779031242456-c2ca2458e8c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxhc2lhbiUyMGNhcnBlbnRlciUyMHdvb2R3b3JraW5nJTIwY2hhaXIlMjB3b3Jrc2hvcCUyMGZ1cm5pdHVyZSUyMG1ha2VyfGVufDB8fHx8MTc5MzU2MTI5N3ww&ixlib=rb-4.1.0&q=85",
    text: "Manufacturers hold world-class craft, but stay disconnected from the designers and markets that could help them create products buyers want. Without the right creative support, product development becomes costly—and every new product is a gamble.",
    color: "#D7E85C",
    anim: { rotate: [0, -5, 0, -3, 0], y: [0, -7, 0, -4, 0] },
    dur: 2.6,
  },
  {
    n: "03",
    role: "The Designer",
    icon: "/designer-s.png",
    img: "https://images.unsplash.com/photo-1640622308122-b1b0f3cd5a7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxkZXNpZ25lciUyMHdvcmtpbmclMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODYyNDc1MDV8MA&ixlib=rb-4.1.0&q=85",
    text: "Designers have bold ideas and endless creativity, but lack the production capacity and capital to bring them to market. Without the right bridge to manufacturing and markets, brilliant concepts stay stuck in sketchbooks.",
    color: "#EBC9B5",
    anim: { rotate: [0, -5, 0, -3, 0], y: [0, -7, 0, -4, 0] },
    dur: 2.4,
  },
];

export const Problems = () => {
  const { site } = useSite();
  const textFor = (key, fallback) => t(site, `problems.${key}`, fallback);
  return (
  <section id="problems" data-testid="problems-section" className="relative">
    <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-20 md:py-32">
      {/* heading */}
      <Reveal>
        <span className="text-sm font-bold uppercase tracking-[0.25em] text-[#EBC9B5]">
          The Reality
        </span>
        <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.85] text-[16vw] md:text-[9vw] text-[#EBE6E0]">
          {textFor("title", "THE PROBLEMS.")}
        </h2>
        <p className="mt-6 max-w-xl text-lg text-[#FFFFFF] leading-relaxed">
          Three people. One broken chain. The product economy is full of talent that never meets
          opportunity.
        </p>
        <div className="mt-8 text-right">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#D7E85C]">
            Need solutions?
          </p>
          <p className="mt-4 text-[#FFFFFF]">
            The community is here to help.
          </p>
          <button
            data-testid="propose-projects-btn"
            onClick={() => window.open("/#problems", "_blank", "noopener")}
            className="mt-4 inline-flex items-center bg-[#D7E85C] text-[#1F3864] px-6 py-3 rounded-full font-bold uppercase text-sm tracking-wide hover:bg-[#FFFFFF] active:scale-95 transition-[background,transform] duration-200"
          >
            Propose Projects
          </button>
        </div>
      </Reveal>

      {/* items — icon on the FAR LEFT (same column as the heading) */}
      <div className="mt-16 md:mt-28 flex flex-col gap-24 md:gap-36">
        {ITEMS.map((it, i) => {
          const text = textFor(
            it.n === "01" ? "buyer_text" : it.n === "02" ? "manufacturer_text" : "designer_text",
            it.text
          );
          return (
          <Reveal key={it.n} delay={0.05}>
            <div className="grid md:grid-cols-[minmax(220px,340px)_1fr] gap-8 md:gap-14 items-center">
              {/* animated icon with soft oatmeal spotlight */}
              <div className="relative aspect-square w-full max-w-[340px] grid place-items-center">
                <div className="absolute inset-[3%] rounded-full bg-[#EBE6E0] blur-2xl opacity-90" />
                <div className="absolute inset-[20%] rounded-full bg-[#EBE6E0] blur-xl" />
                <motion.img
                  src={it.icon}
                  alt={`${it.role} icon`}
                  data-testid={`problem-icon-${i}`}
                  className="relative w-[84%] h-[84%] object-contain"
                  animate={it.anim}
                  transition={{ duration: it.dur, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>

              {/* media + caption */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl border border-white/10">
                  <div className="absolute z-10 top-4 left-4 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-widest text-white/90 bg-black/40 backdrop-blur px-2 py-1 rounded">
                      Loop · video placeholder
                    </span>
                  </div>
                  <motion.img
                    src={it.img}
                    alt={it.role}
                    data-testid={`problem-media-${i}`}
                    className="w-full h-[46vh] md:h-[64vh] object-cover grayscale group-hover:grayscale-0 brightness-110 contrast-95 hue-rotate-15 transition-[filter] duration-700"
                    initial={{ scale: 1.15 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <div className="mt-6 flex items-start gap-5">
                  <span className="font-display font-black text-3xl" style={{ color: it.color }}>
                    {it.n}
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-2xl md:text-4xl text-[#EBE6E0]">{it.role}</h3>
                    <p className="mt-3 text-base md:text-lg text-[#FFFFFF] max-w-xl leading-relaxed">
                      {text}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          );
        })}
      </div>
    </div>
  </section>
  );
};
